import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Avatar,
  Tooltip,
} from "@mui/material";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import { PROJECTS_ROUTE } from "../helper/projects";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import MonitorHeartOutlinedIcon from "@mui/icons-material/MonitorHeartOutlined";
import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import axios from "axios";
import { fetchRights } from "./AdminRoute";
import { displayName } from "../helper/displayName";
import { fetchModuleAccess } from "./ModuleRoute";
import { ITEM_KIND_STYLES } from "./itemKindStyles";
import { REPRODUCTIONS_BASE } from "../helper/reproductionFlow";
import { SELECTIONS_BASE } from "../helper/selectionFlow";
import {
  MODULE_COLOR_EXPLORER,
  MODULE_IMAGE_REPRODUCTION,
  MODULE_SELECTION_EXPLORER,
} from "../helper/moduleIds";

const CALIBRATION_PATHS = ["/cameracalibrations", "/colorexplorationcalibrationcamera"];

/** Resource pages (lasers, substrates, calibrations) are shared by all modules. */
const RESOURCE_PATHS = ["/lasers", "/substrates", ...CALIBRATION_PATHS];
const isResourcePath = (path) => RESOURCE_PATHS.includes(path);

/** Name of the module the given page belongs to (null outside any module).
 *  Resource pages belong to no module of their own — see currentModule. */
const moduleForPath = (path) => {
  if (isResourcePath(path)) return null;
  if (path.startsWith("/colorexploration")) return "Color Explorer";
  if (path.startsWith("/colorselection")) return "Color Selection";
  if (path.startsWith("/colorreproduction")) return "Color Reproduction";
  return null;
};

/** Heading for pages outside any module, so the panel header (and with it
 *  the Resources section below) sits at the same height on every page.
 *  The resource pages use theirs when no module was visited before. */
const PAGE_HEADINGS = {
  "/": { kicker: "Overview", title: "Home" },
  "/projects": { kicker: "Workspace", title: "Projects" },
  "/profile": { kicker: "Account", title: "Profile" },
  "/passwordchange": { kicker: "Account", title: "Password" },
  "/users": { kicker: "Administration", title: "Users" },
  "/system": { kicker: "Administration", title: "System" },
  "/login": { kicker: "Account", title: "Login" },
  "/passwordforgotten": { kicker: "Account", title: "Password" },
  "/passwordreset": { kicker: "Account", title: "Password" },
  "/about": { kicker: "Page", title: "About" },
  "/contact": { kicker: "Page", title: "Contact" },
  "/lasers": { kicker: "Resource", title: "Lasers" },
  "/substrates": { kicker: "Resource", title: "Substrates" },
  "/cameracalibrations": { kicker: "Resource", title: "Calibrations" },
  "/colorexplorationcalibrationcamera": { kicker: "Resource", title: "Calibrations" },
};

/** The module last visited is remembered so that the shared resource pages
 *  keep showing the module the user came from. */
const LAST_MODULE_KEY = "sidenav-last-module";

/** The menu never scrolls. When the viewport is too short for everything,
 *  these sections are dropped in this order until the rest fits; both stay
 *  one click away on the landing page. */
const DROPPED_WHEN_SHORT = ["Modules", "Resources"];

/** Projects span the modules (a folder holds explorations, selections and
 *  reproductions), so the entry is shown on every page for signed-in users,
 *  above the resources. */
const WORKSPACE_SECTION = {
  title: "Workspace",
  items: [
    {
      label: "Projects",
      icon: <FolderOutlinedIcon fontSize="small" />,
      to: PROJECTS_ROUTE,
      isActive: (path) => path === PROJECTS_ROUTE || path.startsWith(`${PROJECTS_ROUTE}/`),
    },
  ],
};

/** Resources are shared by all modules and therefore shown on every page
 *  (for logged-in users); the module sections only in their module. */
const RESOURCES_SECTION = {
  title: "Resources",
  items: [
    {
      label: "Lasers",
      icon: <BoltOutlinedIcon fontSize="small" />,
      to: "/lasers",
      isActive: (path) => path === "/lasers",
    },
    {
      label: "Substrates",
      icon: <LayersOutlinedIcon fontSize="small" />,
      to: "/substrates",
      isActive: (path) => path === "/substrates",
    },
    {
      label: "Calibrations",
      icon: <PaletteOutlinedIcon fontSize="small" />,
      to: "/cameracalibrations",
      isActive: (path) => CALIBRATION_PATHS.includes(path),
    },
  ],
};

const ExplorationIcon = ITEM_KIND_STYLES.exploration.Icon;
const SelectionIcon = ITEM_KIND_STYLES.selection.Icon;
const ReproductionIcon = ITEM_KIND_STYLES.reproduction.Icon;
/** The module entries carry their module's colour on the icon. */
const MODULE_ICON_SX = (kind) => ({ color: ITEM_KIND_STYLES[kind].accent });

/** The three modules, each opening its combined overview list (every type
 *  of selection or reproduction in one list; the type sits on the card).
 *  Shown on every page for signed-in users, below the resources; the entry
 *  of the module the current page belongs to is selected, a module the
 *  account has no access row for is disabled. */
const MODULE_ENTRIES = [
  {
    label: "Color Explorer",
    moduleId: MODULE_COLOR_EXPLORER,
    icon: <ExplorationIcon fontSize="small" sx={MODULE_ICON_SX("exploration")} />,
    to: "/colorexplorationhome",
    // Every exploration page except the calibration pages (list, wizard,
    // analyse, summaries, measurements).
    isActive: (path) => path.startsWith("/colorexploration") && !CALIBRATION_PATHS.includes(path),
  },
  {
    label: "Color Selection",
    moduleId: MODULE_SELECTION_EXPLORER,
    icon: <SelectionIcon fontSize="small" sx={MODULE_ICON_SX("selection")} />,
    to: SELECTIONS_BASE,
    isActive: (path) => path.startsWith("/colorselection"),
  },
  {
    label: "Color Reproduction",
    moduleId: MODULE_IMAGE_REPRODUCTION,
    icon: <ReproductionIcon fontSize="small" sx={MODULE_ICON_SX("reproduction")} />,
    to: REPRODUCTIONS_BASE,
    isActive: (path) => path.startsWith("/colorreproduction"),
  },
];

const itemButtonSx = {
  borderRadius: 2,
  mb: 0.25,
  "&:hover": { bgcolor: "rgba(255,255,255,0.06)" },
  "&.Mui-selected": {
    bgcolor: "rgba(76, 126, 255, 0.32)",
    "&:hover": { bgcolor: "rgba(76, 126, 255, 0.4)" },
  },
};

const itemTextProps = (active) => ({
  fontSize: 14,
  fontWeight: active ? 700 : 500,
  color: active ? "#ffffff" : "rgba(255,255,255,0.85)",
});

const itemIconSx = (active) => ({
  minWidth: 34,
  color: active ? "#cfe0ff" : "rgba(255,255,255,0.6)",
});

/**
 * Persistent left-hand navigation, shown on every page. Top: the page or
 * module heading and the app sections. Bottom: the account area — avatar
 * and name with the Profile/Users/System/Logout entries when logged in, a
 * Login entry otherwise. It is viewport-bound and never scrolls: on a short
 * viewport the Modules and then the Resources section give way (see
 * DROPPED_WHEN_SHORT). The environment warning and the backend status live
 * in the top bar (NavBarTop).
 */
export default function SideNav() {
  const location = useLocation();
  const navigate = useNavigate();

  const token = localStorage.getItem("token") || "";
  const username = localStorage.getItem("username") || "";
  const loggedIn = token.length > 0;

  const [avatarUrl, setAvatarUrl] = useState(null);

  useEffect(() => {
    let objectUrl = null;
    let cancelled = false;

    const loadAvatar = async () => {
      if (!loggedIn) {
        setAvatarUrl(null);
        return;
      }
      try {
        const response = await axios.get("api/user/avatar", { responseType: "blob" });
        if (cancelled) return;
        objectUrl = URL.createObjectURL(response.data);
        setAvatarUrl(objectUrl);
      } catch (error) {
        // 404 = no image set yet — the letter fallback is shown instead.
        if (!cancelled) setAvatarUrl(null);
      }
    };

    loadAvatar();
    // The Profile page fires this after a successful upload.
    window.addEventListener("avatar-updated", loadAvatar);
    return () => {
      cancelled = true;
      window.removeEventListener("avatar-updated", loadAvatar);
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [loggedIn]);

  // Administrators get the Users and System entries below Profile, developers
  // the System entry only (see AdminRoute). The same lookup brings the first
  // name shown in the account row; the Profile page fires "profile-updated"
  // after a save so the row follows a renamed account.
  const [rights, setRights] = useState({ isAdmin: false, isDeveloper: false, firstName: "" });
  useEffect(() => {
    let cancelled = false;
    if (!loggedIn) {
      setRights({ isAdmin: false, isDeveloper: false, firstName: "" });
      return undefined;
    }
    const load = () =>
      fetchRights()
        .then((result) => {
          if (!cancelled) setRights(result);
        })
        .catch(() => {
          if (!cancelled) setRights({ isAdmin: false, isDeveloper: false, firstName: "" });
        });
    load();
    window.addEventListener("profile-updated", load);
    return () => {
      cancelled = true;
      window.removeEventListener("profile-updated", load);
    };
  }, [loggedIn]);
  const showUsers = rights.isAdmin;
  const showSystem = rights.isAdmin || rights.isDeveloper;

  // Module access (the same lookup the module routes make): a module the
  // account has no access row for is shown disabled. Null while unknown
  // (signed out, no stored user id, lookup failed): nothing is disabled.
  const [accessibleModules, setAccessibleModules] = useState(null);
  useEffect(() => {
    const userId = localStorage.getItem("userId");
    if (!loggedIn || !userId || userId === "null" || userId === "undefined") {
      setAccessibleModules(null);
      return undefined;
    }
    let cancelled = false;
    Promise.resolve()
      .then(() => fetchModuleAccess(userId))
      .then((modules) => {
        if (!cancelled && Array.isArray(modules)) setAccessibleModules(new Set(modules));
      })
      .catch(() => {
        if (!cancelled) setAccessibleModules(null);
      });
    return () => {
      cancelled = true;
    };
  }, [loggedIn]);
  const modulesSection = {
    title: "Modules",
    items: MODULE_ENTRIES.map((entry) => {
      const restricted = accessibleModules !== null && !accessibleModules.has(entry.moduleId);
      return { ...entry, disabled: restricted, hint: restricted ? "Access restricted" : null };
    }),
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("username");
    localStorage.removeItem("userId");
    navigate("/");
    window.location.reload();
  };

  const pathModule = moduleForPath(location.pathname);
  // On a shared resource page keep the module the user came from (if any).
  const currentModule =
    pathModule ??
    (isResourcePath(location.pathname) ? sessionStorage.getItem(LAST_MODULE_KEY) : null);
  useEffect(() => {
    if (pathModule) sessionStorage.setItem(LAST_MODULE_KEY, pathModule);
    else if (!isResourcePath(location.pathname)) sessionStorage.removeItem(LAST_MODULE_KEY);
  }, [pathModule, location.pathname]);

  // /passwordreset/<uid>/<token> carries the email link's parameters.
  const headingKey = location.pathname.startsWith("/passwordreset/")
    ? "/passwordreset"
    : location.pathname.startsWith(`${PROJECTS_ROUTE}/`)
      ? PROJECTS_ROUTE
      : location.pathname;
  // Always present: a page without a header would shift the sections up.
  const heading = currentModule
    ? { kicker: "Module", title: currentModule }
    : PAGE_HEADINGS[headingKey] || PAGE_HEADINGS["/"];

  // The same three sections on every page; the module the page belongs to
  // shows in the panel header and as the selected Modules entry.
  const sections = loggedIn ? [WORKSPACE_SECTION, RESOURCES_SECTION, modulesSection] : [];

  // Fit to height: after every layout, while the menu overflows, one more
  // of DROPPED_WHEN_SHORT is hidden. A change of the viewport (the menu is
  // as tall as the viewport minus the top bar) or of the entries starts over
  // from everything shown; the layout effects settle before the paint, so
  // nothing flickers.
  const navRef = useRef(null);
  const [dropped, setDropped] = useState(0);
  const [layoutTick, setLayoutTick] = useState(0);
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return undefined;
    const bump = () => setLayoutTick((tick) => tick + 1);
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", bump);
      return () => window.removeEventListener("resize", bump);
    }
    const observer = new ResizeObserver(bump);
    observer.observe(nav);
    return () => observer.disconnect();
  }, []);
  useLayoutEffect(() => {
    setDropped(0);
  }, [layoutTick, loggedIn, showUsers, showSystem, accessibleModules]);
  useLayoutEffect(() => {
    const nav = navRef.current;
    if (!nav || dropped >= DROPPED_WHEN_SHORT.length) return;
    if (nav.scrollHeight > nav.clientHeight + 1) setDropped(dropped + 1);
  });
  const hiddenTitles = DROPPED_WHEN_SHORT.slice(0, dropped);
  const shownSections = sections.filter((section) => !hiddenTitles.includes(section.title));

  return (
    <Box
      component="nav"
      ref={navRef}
      sx={{
        width: 232,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        // The row container is viewport-bound, so the menu never grows with
        // the page content; what does not fit is dropped, never scrolled.
        height: "100%",
        overflow: "hidden",
        boxSizing: "border-box",
        // Same dark navy family as the top menu bar
        // (#0d1117 -> #121a26 there), lifted a step brighter.
        background: "linear-gradient(180deg, #1a2230 0%, #202b3d 100%)",
        borderRight: "1px solid rgba(255,255,255,0.08)",
        pt: 2,
        pb: 2,
      }}
    >
      {/* Panel header: the module the user is working in, or the page name
          outside any module (same height everywhere, so nothing jumps). */}
      <Box sx={{ px: 2.5, mb: 1 }}>
          <Typography
            variant="overline"
            sx={{
              display: "block",
              color: "rgba(255,255,255,0.45)",
              fontWeight: 600,
              letterSpacing: 1,
              lineHeight: 1.6,
            }}
          >
            {heading.kicker}
          </Typography>
          <Typography
            sx={{
              color: "#ffffff",
              fontSize: 17,
              fontWeight: 800,
              lineHeight: 1.3,
            }}
          >
            {heading.title}
          </Typography>
          <Divider sx={{ mt: 1.5, borderColor: "rgba(255,255,255,0.1)" }} />
      </Box>

      {shownSections.map((section, index) => (
        <Box key={section.title}>
          {index > 0 ? (
            <Divider sx={{ my: 1.5, mx: 2, borderColor: "rgba(255,255,255,0.1)" }} />
          ) : null}
          <Typography
            variant="overline"
            sx={{
              px: 2.5,
              color: "rgba(255,255,255,0.55)",
              fontWeight: 700,
              letterSpacing: 1,
            }}
          >
            {section.title}
          </Typography>
          <List dense sx={{ px: 1, py: 0.5 }}>
            {section.items.map((item) => {
              const active = item.isActive(location.pathname);
              const button = (
                <ListItemButton
                  selected={active}
                  disabled={Boolean(item.disabled)}
                  onClick={() => navigate(item.to)}
                  sx={itemButtonSx}
                >
                  <ListItemIcon sx={itemIconSx(active)}>{item.icon}</ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    secondary={item.hint || null}
                    primaryTypographyProps={itemTextProps(active)}
                    secondaryTypographyProps={{ fontSize: 11, color: "rgba(255,255,255,0.45)" }}
                  />
                </ListItemButton>
              );
              // A disabled button swallows pointer events: the tooltip sits on a span.
              return item.hint ? (
                <Tooltip key={item.label} title={item.hint} placement="right">
                  <span>{button}</span>
                </Tooltip>
              ) : (
                <React.Fragment key={item.label}>{button}</React.Fragment>
              );
            })}
          </List>
        </Box>
      ))}

      {/* Account area, pinned to the bottom. */}
      <Box sx={{ mt: "auto", pt: 1 }}>
        <Divider sx={{ mb: 1.5, mx: 2, borderColor: "rgba(255,255,255,0.1)" }} />
        {loggedIn ? (
          <Box sx={{ px: 1 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                px: 1,
                py: 0.75,
                mb: 0.5,
                borderRadius: 2,
                bgcolor: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.06)",
              }}
            >
              <Avatar
                src={avatarUrl || undefined}
                sx={{
                  width: 34,
                  height: 34,
                  fontSize: 15,
                  bgcolor: "rgba(76, 126, 255, 0.45)",
                  color: "#ffffff",
                  fontWeight: 700,
                  border: "2px solid rgba(255,255,255,0.18)",
                }}
              >
                {(username || "?").charAt(0).toUpperCase()}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="overline"
                  sx={{ display: "block", color: "rgba(255,255,255,0.45)", fontWeight: 600, letterSpacing: 1, lineHeight: 1.4 }}
                >
                  Signed in
                </Typography>
                <Typography
                  noWrap
                  title={username}
                  sx={{ color: "#ffffff", fontSize: 13.5, fontWeight: 700, lineHeight: 1.3 }}
                >
                  {displayName({ first_name: rights.firstName, username })}
                </Typography>
              </Box>
            </Box>
            <List dense sx={{ py: 0 }}>
              <ListItemButton
                selected={location.pathname === "/profile"}
                onClick={() => navigate("/profile")}
                sx={itemButtonSx}
              >
                <ListItemIcon sx={itemIconSx(location.pathname === "/profile")}>
                  <AccountCircleOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Profile"
                  primaryTypographyProps={itemTextProps(location.pathname === "/profile")}
                />
              </ListItemButton>
              {showUsers ? (
                <ListItemButton
                  selected={location.pathname === "/users"}
                  onClick={() => navigate("/users")}
                  sx={itemButtonSx}
                >
                  <ListItemIcon sx={itemIconSx(location.pathname === "/users")}>
                    <ManageAccountsOutlinedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Users"
                    primaryTypographyProps={itemTextProps(location.pathname === "/users")}
                  />
                </ListItemButton>
              ) : null}
              {showSystem ? (
                <ListItemButton
                  selected={location.pathname === "/system"}
                  onClick={() => navigate("/system")}
                  sx={itemButtonSx}
                >
                  <ListItemIcon sx={itemIconSx(location.pathname === "/system")}>
                    <MonitorHeartOutlinedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="System"
                    primaryTypographyProps={itemTextProps(location.pathname === "/system")}
                  />
                </ListItemButton>
              ) : null}
              <ListItemButton onClick={handleLogout} sx={itemButtonSx}>
                <ListItemIcon sx={itemIconSx(false)}>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Logout" primaryTypographyProps={itemTextProps(false)} />
              </ListItemButton>
            </List>
          </Box>
        ) : (
          <List dense sx={{ px: 1, py: 0 }}>
            <ListItemButton
              selected={location.pathname === "/login"}
              onClick={() => navigate("/login")}
              sx={itemButtonSx}
            >
              <ListItemIcon sx={itemIconSx(location.pathname === "/login")}>
                <LoginIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Login"
                primaryTypographyProps={itemTextProps(location.pathname === "/login")}
              />
            </ListItemButton>
          </List>
        )}
        <Typography
          variant="caption"
          sx={{
            display: "block",
            textAlign: "center",
            mt: 1.5,
            color: "rgba(255,255,255,0.4)",
          }}
        >
          © {new Date().getFullYear()} Oraclase
        </Typography>
      </Box>
    </Box>
  );
}
